<!-- Shared date range picker -->
<div [class.disabled-container]="isDisabled">
	<form [formGroup]="formGroup" class="date-range-form">
		<div
			class="date-range-fields"
			[class.has-field-labels]="showFieldLabels"
		>
			<div class="date-range-field">
				<label
					*ngIf="showFieldLabels"
					[for]="id + '-from'"
				>
					{{ startLabel }}
				</label>
				<div
					class="input-group date-control"
					[class.is-open]="startDatepicker.isOpen()"
				>
					<input
						class="form-control"
						[id]="id + '-from'"
						placeholder="yyyy-mm-dd"
						[name]="id + '-from'"
						[value]="formatter.format(fromDate)"
						ngbDatepicker
						#startDatepicker="ngbDatepicker"
						autoClose="outside"
						navigation="arrows"
						[footerTemplate]="startCalendarNavigation"
						[minDate]="datepickerMinDate"
						[maxDate]="datepickerMaxDate"
						(dateSelect)="onStartDateSelection($event)"
						(navigate)="syncCalendarNavigation($event, 'start')"
						[displayMonths]="1"
						[dayTemplate]="dayTemplate"
						outsideDays="hidden"
						container="body"
						datepickerClass="ui-v2-datepicker"
						[restoreFocus]="focusRestoreTarget"
						placement="bottom-start top-start bottom-end top-end"
						(closed)="onCalendarClosed('start')"
						(click)="toggleCalendar(startDatepicker, endDatepicker, 'start')"
						[disabled]="isDisabled"
						[attr.aria-expanded]="startDatepicker.isOpen()"
						aria-haspopup="dialog"
						[attr.aria-label]="startLabel"
						readonly
					/>
					<button
						class="btn btn-outline-primary calendar"
						[class.is-open]="startDatepicker.isOpen()"
						[disabled]="isDisabled"
						(click)="toggleCalendar(startDatepicker, endDatepicker, 'start')"
						type="button"
						[attr.aria-expanded]="startDatepicker.isOpen()"
						aria-haspopup="dialog"
						[attr.aria-label]="'Open ' + startLabel.toLowerCase() + ' calendar'"
					>
						<ng-fa-icon icon="calendar-days"></ng-fa-icon>
					</button>
				</div>
			</div>

			<span class="date-range-separator" aria-hidden="true">
				<ng-fa-icon icon="arrow-right"></ng-fa-icon>
			</span>

			<div class="date-range-field">
				<label
					*ngIf="showFieldLabels"
					[for]="id + '-to'"
				>
					{{ endLabel }}
				</label>
				<div
					class="input-group date-control"
					[class.is-open]="endDatepicker.isOpen()"
				>
					<input
						class="form-control"
						[id]="id + '-to'"
						placeholder="yyyy-mm-dd"
						[name]="id + '-to'"
						[value]="formatter.format(toDate)"
						ngbDatepicker
						#endDatepicker="ngbDatepicker"
						autoClose="outside"
						navigation="arrows"
						[footerTemplate]="endCalendarNavigation"
						[minDate]="datepickerMinDate"
						[maxDate]="datepickerMaxDate"
						[markDisabled]="isEndDateDisabled"
						(dateSelect)="onEndDateSelection($event)"
						(navigate)="syncCalendarNavigation($event, 'end')"
						[displayMonths]="1"
						[dayTemplate]="dayTemplate"
						outsideDays="hidden"
						container="body"
						datepickerClass="ui-v2-datepicker"
						[restoreFocus]="focusRestoreTarget"
						placement="bottom-start top-start bottom-end top-end"
						(closed)="onCalendarClosed('end')"
						(click)="toggleCalendar(endDatepicker, startDatepicker, 'end')"
						[disabled]="isDisabled"
						[attr.aria-expanded]="endDatepicker.isOpen()"
						aria-haspopup="dialog"
						[attr.aria-label]="endLabel"
						readonly
					/>
					<button
						class="btn btn-outline-primary calendar"
						[class.is-open]="endDatepicker.isOpen()"
						[disabled]="isDisabled"
						(click)="toggleCalendar(endDatepicker, startDatepicker, 'end')"
						type="button"
						[attr.aria-expanded]="endDatepicker.isOpen()"
						aria-haspopup="dialog"
						[attr.aria-label]="'Open ' + endLabel.toLowerCase() + ' calendar'"
					>
						<ng-fa-icon icon="calendar-days"></ng-fa-icon>
					</button>
				</div>
			</div>
		</div>

		<ng-template
			#dayTemplate
			let-date
			let-focused="focused"
		>
			<span
				class="custom-day"
				[class.focused]="focused"
				[class.range]="isRange(date)"
				[class.faded]="isHovered(date) || isInside(date)"
				(mouseenter)="hoveredDate = date"
				(mouseleave)="hoveredDate = null"
			>
				{{ date.day }}
			</span>
		</ng-template>
	</form>
</div>

<ng-template #startCalendarNavigation>
	<div class="ui-v2-calendar-navigation">
		<ng-custom-dropdown
			[ngModel]="startCalendarMonth"
			(ngModelChange)="navigateCalendar(startDatepicker, $event, startCalendarYear)"
			[ngModelOptions]="{ standalone: true }"
			[options]="calendarMonthOptions"
			[onlyText]="true"
			[closeOnScroll]="false"
			[container]="null"
			display="static"
			dropdownClass="ui-v2-calendar-menu"
			[elementId]="id + '-start-month'"
		></ng-custom-dropdown>

		<ng-custom-dropdown
			[ngModel]="startCalendarYear"
			(ngModelChange)="navigateCalendar(startDatepicker, startCalendarMonth, $event)"
			[ngModelOptions]="{ standalone: true }"
			[options]="calendarYearOptions"
			[onlyText]="true"
			[closeOnScroll]="false"
			[container]="null"
			display="static"
			dropdownClass="ui-v2-calendar-menu"
			[elementId]="id + '-start-year'"
		></ng-custom-dropdown>
	</div>
</ng-template>

<ng-template #endCalendarNavigation>
	<div class="ui-v2-calendar-navigation">
		<ng-custom-dropdown
			[ngModel]="endCalendarMonth"
			(ngModelChange)="navigateCalendar(endDatepicker, $event, endCalendarYear)"
			[ngModelOptions]="{ standalone: true }"
			[options]="calendarMonthOptions"
			[onlyText]="true"
			[closeOnScroll]="false"
			[container]="null"
			display="static"
			dropdownClass="ui-v2-calendar-menu"
			[elementId]="id + '-end-month'"
		></ng-custom-dropdown>

		<ng-custom-dropdown
			[ngModel]="endCalendarYear"
			(ngModelChange)="navigateCalendar(endDatepicker, endCalendarMonth, $event)"
			[ngModelOptions]="{ standalone: true }"
			[options]="calendarYearOptions"
			[onlyText]="true"
			[closeOnScroll]="false"
			[container]="null"
			display="static"
			dropdownClass="ui-v2-calendar-menu"
			[elementId]="id + '-end-year'"
		></ng-custom-dropdown>
	</div>
</ng-template>
